export const SERVICE_UUID = "12345678-1234-1234-1234-123456789abc";
export const PARAM_UUID   = "abcd1234-5678-90ab-cdef-1234567890ad";
export const BPM_VAR_UUID = "abcd1234-5678-90ab-cdef-1234567890ae";
export const AUDIO_MODE_UUID = "abcd1234-5678-90ab-cdef-1234567890af";
export const DEVSET_SERVICE_UUID = "8f0c0001-5a1f-4b6a-9b12-3f1c8b2d4c10";
export const DEVSET_INFO_UUID = "8f0c0002-5a1f-4b6a-9b12-3f1c8b2d4c10";

const UINT32_MAX = 0xFFFFFFFF;
const SIMPLE_HEARTBEAT_MODE_PACKET = new Uint8Array([0x00, 0x00, 0xe8, 0x03, 0x01, 0x00, 0x00, 0x00]);

let paramCharacteristic = null;
let bpmVarianceCharacteristic = null;
let audioModeCharacteristic = null;
let device = null;
let server = null;
let disconnectCallback = null;

export function onBleDisconnect(cb) {
  disconnectCallback = cb;
}

export async function connectDevice() {
  if (!navigator.bluetooth) {
    throw new Error("Web Bluetooth not supported");
  }

  device = await navigator.bluetooth.requestDevice({
    filters: [{ namePrefix: "LH" }],
    optionalServices: [SERVICE_UUID, DEVSET_SERVICE_UUID]
  });

  device.addEventListener("gattserverdisconnected", handleDisconnect);

  server = await device.gatt.connect();
  const service = await server.getPrimaryService(SERVICE_UUID);

  paramCharacteristic = await service.getCharacteristic(PARAM_UUID);
  bpmVarianceCharacteristic = await service.getCharacteristic(BPM_VAR_UUID);
  audioModeCharacteristic = await service.getCharacteristic(AUDIO_MODE_UUID).catch(() => null);

  console.log("✅ BLE connected");
}

function handleDisconnect() {
  console.warn("⚠️ BLE disconnected unexpectedly");

  cleanup();

  if (disconnectCallback) {
    disconnectCallback();
  }
}

export async function disconnectDevice() {
  if (device?.gatt?.connected) {
    device.gatt.disconnect();
  }
  cleanup();
}

function cleanup() {
  device = null;
  server = null;
  paramCharacteristic = null;
  bpmVarianceCharacteristic = null;
  audioModeCharacteristic = null;
}

function buildParameterPacket({
  bpm,
  varianceS1S1,
  varianceS1S2,
  amplitudePct,
  ledBpm,
  ledIntensity
}) {
  const buffer = new ArrayBuffer(28);
  const view = new DataView(buffer);

  const dtS1S1_ms = Math.round(60000 / bpm);
  const dtS1S2_ms = computeDtS1S2Ms(bpm);

  view.setUint32(0,  dtS1S1_ms, true); // dtS1S1 ms
  view.setUint32(4,  dtS1S2_ms, true); // dtS1S2 ms
  view.setUint32(8,  Math.round(varianceS1S2 * 10000), true);
  view.setUint32(12, Math.round(varianceS1S1 * 10000), true);

  // Firmware divides by 100 → send percent
  view.setUint32(16, Math.round(amplitudePct), true);

  view.setUint32(20, Math.round(ledBpm), true);
  view.setUint32(24, Math.round(ledIntensity), true);

  return buffer;
}

export async function sendParameters(params) {
  if (!paramCharacteristic) {
    throw new Error("Not connected");
  }

  const packet = buildParameterPacket(params);
  await paramCharacteristic.writeValueWithResponse(packet);

  console.log("📤 Params sent", params);
}

function computeDtS1S2Ms(bpm) {
  return Math.round(
    (0.817 + (-9.31e-3 * bpm) + (3.34e-5 * bpm * bpm)) * 1000
  );
}

export async function forceSimpleHeartbeatMode() {
  if (!audioModeCharacteristic && server) {
    const service = await server.getPrimaryService(SERVICE_UUID);
    audioModeCharacteristic = await service.getCharacteristic(AUDIO_MODE_UUID);
  }

  if (!audioModeCharacteristic) {
    throw new Error("Audio mode characteristic not available");
  }

  await audioModeCharacteristic.writeValueWithResponse(SIMPLE_HEARTBEAT_MODE_PACKET);

  console.log("Simple heartbeat mode enabled");
}

export async function disableBpmVariance() {
  if (!bpmVarianceCharacteristic) {
    throw new Error("Not connected");
  }

  const buffer = new ArrayBuffer(12);
  const view = new DataView(buffer);

  view.setFloat32(0, 0.0, true);          // variancePct
  view.setUint32(4, UINT32_MAX, true);    // interval
  view.setUint32(8, 0, true);              // transition

  await bpmVarianceCharacteristic.writeValueWithResponse(buffer);

  console.log("🔕 BPM variance disabled");
}

export async function readParameters() {
  if (!paramCharacteristic) return null;

  const value = await paramCharacteristic.readValue();
  if (value.byteLength < 28) return null;

  const view = new DataView(value.buffer);

  const dtS1S1 = view.getUint32(0, true);
  const dtS1S2 = view.getUint32(4, true);
  const varS1S2 = view.getUint32(8, true) / 10000;
  const varS1S1 = view.getUint32(12, true) / 10000;

  // ⚠️ firmware currently divides by 100, Android divides by 10000
  const rawAmp = view.getUint32(16, true) / 100;
  const amp = normaliseAmplitude(rawAmp);

  const ledBpm = view.getUint32(20, true);
  const ledIntensity = view.getUint32(24, true);

  return {
    dtS1S1,
    dtS1S2,
    varS1S1,
    varS1S2,
    amp,
    ledBpm,
    ledIntensity
  };
}

export async function readFirmwareInfo() {
  if (!server) return null;

  try {
    const service = await server.getPrimaryService(DEVSET_SERVICE_UUID);
    const ch = await service.getCharacteristic(DEVSET_INFO_UUID);
    const value = await ch.readValue();
    const bytes = new Uint8Array(value.buffer, value.byteOffset, value.byteLength);
    const txt = new TextDecoder("utf-8").decode(bytes).trim();
    if (!txt) return null;

    const obj = JSON.parse(txt);
    return {
      device_id: String(obj.device_id ?? ""),
      fw: String(obj.fw ?? ""),
      fw_id: Number(obj.fw_id ?? -1),
      provisioned: Number(obj.provisioned ?? 0),
      raw: txt
    };
  } catch {
    return null;
  }
}

export async function readBpmVariance() {
  if (!bpmVarianceCharacteristic) return null;

  const value = await bpmVarianceCharacteristic.readValue();
  if (value.byteLength < 12) return null;

  const view = new DataView(value.buffer);

  return {
    pct: view.getFloat32(0, true),
    intervalMs: view.getUint32(4, true),
    transitionMs: view.getUint32(8, true)
  };
}

  function normaliseAmplitude(rawAmp) {
    // rawAmp is firmware amp after division
    // valid amp range is 0.0 – 1.0

    if (rawAmp > 1.5) {
      // Almost certainly came from /10000 path
      return rawAmp / 100;
    }

    return rawAmp;
  }



